import type BaseModel from "../models/BaseModel";
import type Connection from "../models/Connection";
import type Consumer from "../models/Consumer";
import type WireEndpoint from "../models/WireEndpoint";
import type Terminal from "../models/Terminal";
import type Channel from "../models/Channel";

export type VirtualConnection = {
  readonly isVirtual: true;
  readonly id: string;
  readonly type: string;
  readonly color: string;
  readonly isDeleted: false;
  readonly fromDevice: BaseModel;
  readonly toDevice: BaseModel;
  /** Includes the consumer whose stored assignments produced this segment. */
  readonly sourceObjectIds: readonly string[];
  readonly consumers: readonly Consumer[];
  readonly modelConnections: readonly Connection[];
};

export type AnalyserConnection = Connection | VirtualConnection;

/** Identifies display-only segments that must never enter model editing or persistence. */
export function isVirtualConnection(connection: AnalyserConnection): connection is VirtualConnection {
  return "isVirtual" in connection && connection.isVirtual === true;
}

/** Deduplicates stable segments while retaining all consumers contributing to them. */
function addVirtualConnection(result: Map<string, AnalyserConnection>, kind: string, from: BaseModel, to: BaseModel,
  owners: readonly string[], color: string, consumer?: Consumer, modelConnection?: Connection): void {
  if (from.isDeleted || to.isDeleted || from.id === to.id) return;
  const identity: (string | number)[] = [kind, from.id, to.id];
  const id = `virtual:${JSON.stringify(identity)}`;
  const previous = result.get(id);
  const sourceObjectIds = new Set([from.id, to.id, ...owners]);
  const consumers = new Set<Consumer>(consumer ? [consumer] : []);
  const modelConnections = new Set<Connection>(modelConnection ? [modelConnection] : []);
  if (previous && isVirtualConnection(previous)) {
    for (const owner of previous.sourceObjectIds) sourceObjectIds.add(owner);
    for (const owner of previous.consumers) consumers.add(owner);
    for (const connection of previous.modelConnections) modelConnections.add(connection);
  }
  result.set(id, { isVirtual: true, id, type: kind, color, isDeleted: false,
    fromDevice: from, toDevice: to, sourceObjectIds: [...sourceObjectIds], consumers: [...consumers], modelConnections: [...modelConnections] });
}

/** Removes the stored assignments or real wiring represented by a selected display segment. */
export function deleteVirtualConnection(connection: VirtualConnection): void {
  const fuse = connection.fromDevice.className === "Fuse" ? connection.fromDevice
    : connection.toDevice.className === "Fuse" ? connection.toDevice : undefined;
  if (fuse) {
    for (const consumer of connection.consumers) {
      if (!consumer.isDeleted && consumer.fuse === fuse) consumer.fuse = undefined;
    }
  } else if (connection.type === "ConsumerWire") {
    for (const consumer of connection.consumers) {
      if (!consumer.isDeleted && consumer.getTerminalViaConsumer() === connection.toDevice.parent) {
        consumer.setTerminalViaConsumer(undefined);
      }
    }
  } else if (connection.type === "WireTerminal") {
    const endpoint = connection.fromDevice as WireEndpoint;
    const owner = endpoint.parent as Consumer;
    const terminal = owner?.terminal?.getTerminalBlock()?.terminalFromWireIndex(endpoint.index);
    if (owner && !owner.isDeleted && terminal === connection.toDevice) owner.terminal = undefined;
  } else if (connection.type === "ConsumerTerminal") {
    for (const consumer of connection.consumers) {
      if (!consumer.isDeleted && consumer.terminal === connection.toDevice) consumer.terminal = undefined;
    }
  } else if (connection.type === "TerminalActor" || connection.type === "WireActor") {
    for (const model of connection.modelConnections) if (!model.isDeleted) model.delete();
  }
}

/** Indexes the supplied analysis evidence so each consumer only visits its own connections. */
function connectionsByDevice(connections: readonly Connection[]): Map<string, Connection[]> {
  const result = new Map<string, Connection[]>();
  for (const connection of connections) {
    for (const device of [connection.fromDevice, connection.toDevice]) {
      if (!device) continue;
      const entries = result.get(device.id) ?? [];
      entries.push(connection);
      result.set(device.id, entries);
    }
  }
  return result;
}

type ConsumerSource = { object: BaseModel; terminal?: Terminal; endpoint?: WireEndpoint };

/** Resolves all occupied conductors, including consumers requiring more than one live wire. */
function consumerSources(consumer: Consumer, wireEndpointsVisible: boolean): ConsumerSource[] {
  if (!wireEndpointsVisible) return [{ object: consumer, terminal: consumer.terminal }];
  const via = consumer.getTerminalViaConsumer?.();
  const owner = via ?? consumer;
  const endpoints = owner.wireEndpoints?.asArray() ?? [];
  const result: ConsumerSource[] = [];
  const ownStart = consumer.cableType?.hasGround === false ? 1 : 2;
  const ownCount = consumer.wireRequirement?.numberOfLWiresWithoutPEandN || 1;
  for (const endpoint of endpoints) {
    const assigned = via ? endpoint.outgoing.some(entry => entry.consumer === consumer)
      : endpoint.index >= ownStart && endpoint.index < ownStart + ownCount;
    if (assigned) result.push({ object: endpoint, endpoint, terminal: endpoint.terminal });
  }
  return result.length ? result : [{ object: consumer, terminal: consumer.terminal }];
}

/** Derives wiring segments from assignments without creating or mutating model connections. */
export function buildVirtualConnections(consumers: readonly Consumer[], connections: readonly Connection[],
  visibleTrees: ReadonlySet<string>): AnalyserConnection[] {
  const terminalsVisible = visibleTrees.has("connectionDashboardTerminals");
  const distributionsVisible = visibleTrees.has("connectionDashboardDistributions");
  // With the distribution column hidden, its endpoint rows live in the consumer column.
  const fusesVisible = visibleTrees.has("connectionDashboardFuses");
  const actorsVisible = visibleTrees.has("connectionDashboardActors");
  const wireEndpointsVisible = distributionsVisible || (visibleTrees.has("connectionDashboardConsumers") && (terminalsVisible || fusesVisible || actorsVisible));
  const result = new Map<string, AnalyserConnection>();
  const replaced = new Set<string>();
  const byDevice = connectionsByDevice(connections);
  for (const consumer of consumers) {
    for (const endpoint of consumer.wireEndpoints?.asArray() ?? []) {
      endpoint.connections = [];
      if (wireEndpointsVisible && terminalsVisible && endpoint.terminal && !consumer.isDeleted) {
        // Ownership places this node in the tree; actual consumers are attached by their wiring below.
        addVirtualConnection(result, "WireTerminal", endpoint, endpoint.terminal, [consumer.id], "var(--connectionColorChannel)");
      }
    }
  }
  for (const consumer of consumers) {
    if (consumer.isDeleted) continue;
    // Temporarily disabled with duplicate distribution consumers (18.09.2026).
    // Restore together with the consumer-column condition in connectionDashboardModel.ts.
    // if (distributionsVisible && visibleTrees.has("connectionDashboardConsumers") && consumer.isSubDistribution
    //   && consumer.channelType !== "IntermediateDistribution") {
    //   const first = consumer.wireEndpoints?.asArray().find(endpoint => !endpoint.isDeleted);
    //   if (first) addVirtualConnection(result, "DistributionHint", consumer, first, [consumer.id], "var(--connectionColorChannel)");
    // }
    const fuse = consumer.fuse;
    const evidence = byDevice.get(consumer.id) ?? [];
    for (const source of consumerSources(consumer, wireEndpointsVisible)) {
      const terminal = source.terminal;
      const useTerminal = terminalsVisible && terminal && !terminal.isDeleted;
      const terminalKind = source.endpoint ? "WireTerminal" : "ConsumerTerminal";
      if (source.endpoint && source.endpoint.parent !== consumer) {
        const via = consumer.getTerminalViaConnection?.();
        addVirtualConnection(result, "ConsumerWire", consumer, source.endpoint, [consumer.id], "var(--connectionColorChannel)", consumer, via);
        if (via) replaced.add(via.id);
      }
      if (useTerminal) {
        addVirtualConnection(result, terminalKind, source.object, terminal, [consumer.id], "var(--connectionColorChannel)", consumer);
      }
      let hasActorFuse = false;
      for (const connection of evidence) {
        if (connection.isDeleted || connection.fromDevice?.isDeleted || connection.toDevice?.isDeleted) continue;
        const partner = connection.fromDevice === consumer ? connection.toDevice : connection.fromDevice;
        if (!partner?.isActorChannel) continue;
        const mainsConsumer = consumer.channelType === "suply230V" || consumer.channelType === "suply400V";
        const supplyType = (partner as Channel).parentDevice?.channelType;
        if (fusesVisible && fuse && !fuse.isDeleted && mainsConsumer
          && (supplyType === "suply230V" || supplyType === "suply400V")) {
          // Fixed mains loads terminate at their assigned fuse; the supply actor is only a model placeholder.
          replaced.add(connection.id);
          continue;
        }
        if (useTerminal) {
          replaced.add(connection.id);
          addVirtualConnection(result, terminalKind, source.object, terminal, [consumer.id, partner.id], connection.color, consumer);
          addVirtualConnection(result, "TerminalActor", terminal, partner, [consumer.id], connection.color, consumer, connection);
        } else if (source.endpoint) {
          replaced.add(connection.id);
          addVirtualConnection(result, "WireActor", source.object, partner, [consumer.id], connection.color, consumer, connection);
        }
        if (fuse && !fuse.isDeleted && (actorsVisible || !fusesVisible)) {
          addVirtualConnection(result, "ActorFuse", partner, fuse, [consumer.id], connection.color, consumer);
          hasActorFuse = true;
        }
      }
      if (fusesVisible && fuse && !fuse.isDeleted && !hasActorFuse) {
        const from = useTerminal ? terminal : source.object;
        const kind = useTerminal ? "TerminalFuse" : source.endpoint ? "WireFuse" : "ConsumerFuse";
        addVirtualConnection(result, kind, from, fuse, [consumer.id], "var(--connectionColorChannel)", consumer);
      }
    }
  }
  for (const connection of connections) {
    if (connection.isDeleted || replaced.has(connection.id)) continue;
    if (!wireEndpointsVisible && connection.type === "TerminalVia") continue;
    result.set(connection.id, connection);
  }
  const graph = [...result.values()];
  attachEndpointConnections(graph);
  return graph;
}

/** Shares the graph's edges with each derived node without introducing a second source of truth. */
function attachEndpointConnections(graph: readonly AnalyserConnection[]): void {
  const endpointConnections = new Map<WireEndpoint, VirtualConnection[]>();
  for (const connection of graph) {
    if (!isVirtualConnection(connection)) continue;
    for (const object of [connection.fromDevice, connection.toDevice]) {
      if (object.className !== "WireEndpoint") continue;
      const endpoint = object as WireEndpoint;
      const entries = endpointConnections.get(endpoint) ?? [];
      entries.push(connection);
      endpointConnections.set(endpoint, entries);
    }
  }
  for (const [endpoint, entries] of endpointConnections) endpoint.connections = entries;
}
